import { authStore } from '@services/authStore.service';
import { environment } from '@config/environment.config';
import type { GraphSnapshot } from '@/graph/domain/types';
import type {
  ExecuteEntity,
  ExecuteCheckpointResponse,
  PlanEntity,
  RunEntity,
  RunStageContext,
  RunUIPermissions,
  SetExecutionModeRequest,
  RunLifecycleRequest,
  UpdateExecuteStepRequest,
  UpdateMachineMappingRequest,
  UpdateRunDestinyRequest,
  UpdateRunPlanRequest,
} from '@/types/run-stage.types';

const ILA_API_BASE = environment.getIlaApiBaseUrl();

async function handleResponse<T>(res: Response, url: string): Promise<T> {
  if (res.status === 401 || res.status === 403) {
    throw new Error(`Unauthorized request to ${url}`);
  }
  if (res.status >= 500) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`Server error on ${url}: ${text}`);
  }

  return (await res.json()) as T;
}

async function post<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(`${ILA_API_BASE}${url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authStore.getAuthHeader(),
    },
    body: JSON.stringify(body),
  });

  return handleResponse<T>(res, url);
}

async function get<T>(url: string): Promise<T> {
  const res = await fetch(`${ILA_API_BASE}${url}`, {
    method: 'GET',
    headers: authStore.getAuthHeader(),
  });

  return handleResponse<T>(res, url);
}

export interface AssignableUser {
  _id: string;
  name: string;
  email?: string;
  removedAt?: string;
}

export async function fetchAssignableUsers(
  entityId: string,
  entityType: string,
): Promise<AssignableUser[]> {
  const encodedEntityId = encodeURIComponent(entityId);
  const encodedEntityType = encodeURIComponent(entityType);
  const res = await get<{ data?: { users?: Array<{ user?: AssignableUser }> } }>(
    `/api/users?entityId=${encodedEntityId}&entityType=${encodedEntityType}`,
  );

  const users = res?.data?.users ?? [];
  return users
    .map((entry) => entry?.user)
    .filter((user): user is AssignableUser => Boolean(user?._id));
}

export async function createRunFromVersion(body: {
  version: string;
  name: string;
  experiment?: string;
  assignees?: string[];
  startDate?: string;
  endDate?: string;
  tat?: { value: number | null; unit: 'seconds' | 'minutes' | 'hours' | 'days' | 'weeks' | null };
}): Promise<RunEntity> {
  return post<RunEntity>('/api/v2/run/create', {
    ...body,
    versionId: body.version,
  });
}

export async function createRunPlan(body: {
  runId: string;
  runUid?: string;
  nodes?: Array<{ nodeId: string; nodeName?: string; materialName?: string; containerName?: string }>;
  originalNodes?: Array<{ nodeId: string; nodeName?: string; materialName?: string; containerName?: string }>;
  startTime?: string | null;
  endTime?: string | null;
  duration?: string;
  typeOfExecution?: string;
}): Promise<PlanEntity> {
  return post<PlanEntity>('/api/v2/plan/create', body);
}

export async function createRunExecute(body: {
  runId: string;
  runUid?: string;
  nodes?: Array<{ nodeId: string; nodeName?: string; materialName?: string; containerName?: string }>;
  startTime?: string | null;
  endTime?: string | null;
  duration?: string;
}): Promise<ExecuteEntity> {
  return post<ExecuteEntity>('/api/v2/execute/init', body);
}

export async function getRun(runId: string): Promise<RunEntity> {
  return post<RunEntity>('/api/v2/run/get', { runId });
}

export async function getRunGraphSnapshot(
  runId: string,
  graphType: 'graph' | 'executeGraph' = 'executeGraph',
): Promise<GraphSnapshot> {
  const url = '/api/v2/run/getGraphSnapshot';
  const res = await fetch(`${ILA_API_BASE}${url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authStore.getAuthHeader(),
    },
    body: JSON.stringify({ runId, graphType }),
  });

  if (res.status === 401 || res.status === 403) {
    throw new Error(`Unauthorized request to ${url}`);
  }

  if (!res.ok) {
    const errorBody = await res.json().catch(async () => ({ error: await res.text().catch(() => 'Request failed') }));
    const errorMessage =
      typeof errorBody?.error === 'string'
        ? errorBody.error
        : `Failed to fetch run graph snapshot from ${url}`;
    throw new Error(errorMessage);
  }

  const payload = (await res.json()) as { graph?: GraphSnapshot };
  if (!payload?.graph) {
    throw new Error('Graph snapshot missing in response.');
  }

  return payload.graph;
}

export async function getPlan(runId: string): Promise<PlanEntity | null> {
  const response = await post<PlanEntity[] | PlanEntity | null>('/api/v2/plan/get', { runId });
  if (Array.isArray(response)) {
    return response[0] ?? null;
  }
  return response;
}

export async function getExecute(runId: string): Promise<ExecuteEntity | null> {
  const response = await post<ExecuteEntity[] | ExecuteEntity | null>('/api/v2/execute/get', { runId });
  if (Array.isArray(response)) {
    return response[0] ?? null;
  }
  return response;
}

export async function getRunStageContext(runId: string): Promise<RunStageContext> {
  const [run, plan, execute] = await Promise.all([
    getRun(runId),
    getPlan(runId).catch(() => null),
    getExecute(runId).catch(() => null),
  ]);

  return { run, plan, execute };
}

export async function updateRunPlan(body: UpdateRunPlanRequest): Promise<PlanEntity> {
  return post<PlanEntity>('/api/v2/plan/update', body);
}

export async function updateRunDestiny(body: UpdateRunDestinyRequest): Promise<PlanEntity> {
  return post<PlanEntity>('/api/plan/updateDestiny', body);
}

export async function updateExecuteStep(body: UpdateExecuteStepRequest): Promise<ExecuteEntity> {
  return post<ExecuteEntity>('/api/execute/updateStatus', body);
}

export async function updateMachineMapping(body: UpdateMachineMappingRequest): Promise<PlanEntity> {
  return post<PlanEntity>('/api/v2/plan/update', body);
}

export async function setExecutionMode(body: SetExecutionModeRequest): Promise<PlanEntity> {
  return post<PlanEntity>('/api/v2/plan/update', body);
}

export async function completePlan(runId: string): Promise<PlanEntity> {
  return post<PlanEntity>('/api/v2/plan/update', { runId, completed: true });
}

export async function pauseRun(body: RunLifecycleRequest): Promise<RunEntity> {
  return post<RunEntity>('/api/run/pause', body);
}

export async function resumeRun(body: RunLifecycleRequest): Promise<RunEntity> {
  return post<RunEntity>('/api/run/resume', body);
}

export async function abortRun(body: RunLifecycleRequest): Promise<RunEntity> {
  return post<RunEntity>('/api/run/abort', body);
}

export async function startExecuteStep(body: {
  runId: string;
  stepId: string;
  cloneNumber: number;
  startTime?: string;
}): Promise<ExecuteEntity> {
  return post<ExecuteEntity>('/api/execute/updateStatus', { ...body, status: 'IN_PROGRESS' });
}

export async function skipExecuteStep(body: {
  runId: string;
  stepId: string;
  cloneNumber: number;
}): Promise<ExecuteEntity> {
  return post<ExecuteEntity>('/api/execute/updateStatus', { ...body, status: 'SKIPPED', completed: true });
}

export interface CompleteExecuteStepV2Request {
  runId: string;
  stepId: string;
  payload: {
    status: string;
    realTime?: {
      startTime?: string;
      endTime?: string;
      duration?: string;
      source?: string;
    };
    checkpointResponses?: ExecuteCheckpointResponse[];
    timings?: Array<{ startTime?: string; endTime?: string }>;
    [key: string]: unknown;
  };
}

export interface CompleteExecuteStepV2Response {
  execute: ExecuteEntity;
  step: unknown;
}

export async function completeExecuteStepV2(
  body: CompleteExecuteStepV2Request,
): Promise<CompleteExecuteStepV2Response> {
  return post<CompleteExecuteStepV2Response>('/api/v2/execute/completeStep', body);
}

export async function getRunUIPermissions(runId: string): Promise<RunUIPermissions> {
  return post<RunUIPermissions>('/api/run/ui', { runId, action: 'runDetails' });
}
